import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { getToken } from "firebase/messaging";
import { getMessagingInstance } from "@/lib/firebase";
import { updateDocument } from "@/lib/firestore";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { signInWithGoogle } from "@/lib/googleSignIn";
import { useTranslations } from "@/contexts/I18nContext";
import { userFacingMessage } from "@/lib/errors/userFacingError";
import { AppDownloadBanner } from "@/components/features/AppDownloadBanner";

export function LoginPage() {
  const navigate = useNavigate();
  const t = useTranslations();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleForgotPassword = async () => {
    setError("");
    setResetSent(false);
    const trimmed = email.trim();
    if (!trimmed) {
      setError(t("login.errors.resetEmptyEmail"));
      return;
    }
    try {
      await sendPasswordResetEmail(auth, trimmed);
      setResetSent(true);
    } catch (err: unknown) {
      setError(userFacingMessage(err, t("login.errors.resetFailed")));
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      const { isNewUser } = await signInWithGoogle();
      const pending = window.localStorage.getItem("pending_invite_code");
      if (pending) {
        navigate(`/join?code=${encodeURIComponent(pending)}`);
      } else {
        navigate(isNewUser ? "/onboarding" : "/dashboard");
      }
    } catch (err: unknown) {
      setError(userFacingMessage(err, t("login.errors.googleFailed")));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Register FCM token
      try {
        const messaging = await getMessagingInstance();
        if (messaging) {
          const token = await getToken(messaging, {
            vapidKey: import.meta.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
          });
          if (token) {
            // Find member profile by authUid
            const q = query(
              collection(db, "member_profiles"),
              where("authUid", "==", result.user.uid)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              const profileDoc = snap.docs[0];
              const existing = (profileDoc.data().user_devices as string[]) ?? [];
              if (!existing.includes(token)) {
                await updateDocument("member_profiles", profileDoc.id, {
                  user_devices: [...existing, token],
                });
              }
            }
          }
        }
      } catch {
        // FCM registration is best-effort
      }

      // If the user arrived via a pending invite link, route them back
      // into the join flow so the code is resolved per [[specs/new-user-
      // onboarding-and-join-flow]] Sub-flows B / C.
      const pending = window.localStorage.getItem("pending_invite_code");
      if (pending) {
        navigate(`/join?code=${encodeURIComponent(pending)}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err: unknown) {
      setError(userFacingMessage(err, t("login.errors.emailFailed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-sm">
      <div className="mb-6">
        <AppDownloadBanner />
      </div>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold" style={{ color: "var(--accent-color, #00C7BE)" }}>
          Zoey
        </h1>
        <p className="text-gray-500 mt-2">{t("login.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1">
            {t("auth.email.label")}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
            placeholder={t("auth.email.placeholder")}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-1">
            {t("auth.password.label")}
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={handleForgotPassword}
            className="mt-1.5 text-xs text-gray-500 hover:text-[color:var(--accent-color,#00C7BE)] underline-offset-2 hover:underline"
          >
            {t("login.forgotPassword")}
          </button>
          {resetSent && (
            <p className="mt-1 text-xs text-green-600 dark:text-green-400">
              {t("login.resetSent")}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-lg text-white font-semibold transition-opacity disabled:opacity-50"
          style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
        >
          {loading ? t("login.signingIn") : t("login.signIn")}
        </button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs text-gray-400 uppercase tracking-wider">{t("common.or")}</span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
          className="w-full py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" />
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" />
          </svg>
          {googleLoading ? t("login.signingIn") : t("login.continueWithGoogle")}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-6">
        {t("login.newToZoey")}{" "}
        <Link to="/register" className="text-[color:var(--accent-color,#00C7BE)] font-medium">
          {t("login.createAccount")}
        </Link>
      </p>
    </div>
  );
}
